import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../lib/api';

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [plants, setPlants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [causes, setCauses] = useState([]);
  const [interventionTypes, setInterventionTypes] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const fetchPlants = useCallback(async () => {
    try {
      const response = await api.get('/plants');
      setPlants(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching plants:', error);
      return [];
    }
  }, []);

  const fetchLocations = useCallback(async (plantId) => {
    try {
      const params = plantId && plantId !== 'all' ? { plant_id: plantId } : {};
      const response = await api.get('/locations', { params });
      setLocations(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  }, []);

  const fetchEquipment = useCallback(async (plantId, locationId) => {
    try {
      const params = {};
      if (plantId) params.plant_id = plantId;
      if (locationId) params.location_id = locationId;
      const response = await api.get('/equipment', { params });
      setEquipment(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment:', error);
      return [];
    }
  }, []);

  const fetchSymptoms = useCallback(async () => {
    try {
      const response = await api.get('/symptoms');
      setSymptoms(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching symptoms:', error);
      return [];
    }
  }, []);

  const fetchCauses = useCallback(async () => {
    try {
      const response = await api.get('/causes');
      setCauses(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching causes:', error);
      return [];
    }
  }, []);

  const fetchInterventionTypes = useCallback(async () => {
    try {
      const response = await api.get('/intervention-types');
      setInterventionTypes(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching intervention types:', error);
      return [];
    }
  }, []);

  const fetchCompetencies = useCallback(async () => {
    try {
      const response = await api.get('/competencies');
      setCompetencies(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching competencies:', error);
      return [];
    }
  }, []);

  const fetchArticles = useCallback(async () => {
    try {
      const response = await api.get('/articles');
      setArticles(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching articles:', error);
      return [];
    }
  }, []);

  const initializeData = useCallback(async () => {
    await Promise.all([
      fetchPlants(),
      fetchLocations(),
      fetchSymptoms(),
      fetchCauses(),
      fetchInterventionTypes(),
      fetchCompetencies()
    ]);
  }, [fetchPlants, fetchLocations, fetchSymptoms, fetchCauses, fetchInterventionTypes, fetchCompetencies]);

  const value = {
    plants,
    locations,
    equipment,
    symptoms,
    causes,
    interventionTypes,
    competencies,
    articles,
    selectedPlant,
    selectedLocation,
    setSelectedPlant: (plant) => {
      setSelectedPlant(plant);
      setSelectedLocation(null);
      if (plant && plant !== 'all') {
        fetchLocations(plant);
      } else {
        fetchLocations();
      }
    },
    setSelectedLocation,
    fetchPlants,
    fetchLocations,
    fetchEquipment,
    fetchSymptoms,
    fetchCauses,
    fetchInterventionTypes,
    fetchCompetencies,
    fetchArticles,
    initializeData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
